import { Link } from '@tanstack/react-router'
import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs'
import { useUrlSearchText } from '@/hooks/use-url-search-text'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Search, Truck } from 'lucide-react'
import { useShipments } from '@/api/hooks'
import { useMyProjectsFilter } from '@/hooks/use-my-projects-filter'
import { ShipmentDateBadges } from '@/components/shipment-dates'
import { ProductSummaryList, type ProductSummaryItem } from '@/components/product-summary-list'
import { BlockedCarriersPanel } from '@/components/blocked-carriers-panel'
import { formatMoney, formatStatus, resultCount } from '@/lib/utils'
import type { components } from '@/api/schema'
import { SearchInput } from '@/components/search-input'
import { ListFilter, ListFilters } from '@/components/list-filters'

const STATUS_OPTIONS = [
  'all',
  'pending',
  'ready_to_book',
  'booked',
  'shipped',
  'in_transit',
  'delivered',
]

type ShipmentLineItem = components['schemas']['PurchaseOrderShipmentLineItemRead']

function shipmentLineItemToProductSummaryItem(item: ShipmentLineItem): ProductSummaryItem {
  return {
    id: item.id,
    title: item.line_item?.model,
    quantity: item.quantity,
    productUuid: item.line_item?.product?.uuid,
    imageUrl: item.line_item?.product?.image_url,
    manufacturerName: item.line_item?.product?.manufacturer_name,
    manufacturerUuid: item.line_item?.product?.manufacturer_uuid,
  }
}

function shipmentStatusVariant(status: string) {
  if (status === 'delivered') return 'default' as const
  if (status === 'shipped' || status === 'in_transit') return 'secondary' as const
  return 'outline' as const
}

function ShipmentsTableSkeleton({ rows = 8 }: { rows?: number }) {
  return (
    <>
      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Project</TableHead>
              <TableHead>Shipment</TableHead>
              <TableHead>Dates</TableHead>
              <TableHead>Products</TableHead>
              <TableHead className="text-right">Cost</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: rows }).map((_, i) => (
              <TableRow key={i}>
                <TableCell>
                  <div className="space-y-1.5">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1.5">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-5 w-20" />
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <Skeleton className="h-5 w-24" />
                    <Skeleton className="h-5 w-28" />
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <Skeleton className="h-7 w-full" />
                    <Skeleton className="h-7 w-3/4" />
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex justify-end">
                    <Skeleton className="h-5 w-16" />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="space-y-2 md:hidden">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="space-y-2 rounded-lg border bg-card p-3">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-1/3" />
            <Skeleton className="h-5 w-full" />
          </div>
        ))}
      </div>
    </>
  )
}

export function ShipmentsPage() {
  const [search, setSearch] = useUrlSearchText()
  const [{ status, page }, setFilters] = useQueryStates({
    status: parseAsString.withDefault('all'),
    page: parseAsInteger.withDefault(1),
  })

  const { opsPersonId } = useMyProjectsFilter()
  const { data, isLoading } = useShipments({
    search: search || undefined,
    status: status === 'all' ? undefined : status,
    ops_person_id: opsPersonId,
    page,
    limit: 20,
  })

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">Shipments</h1>

      <Tabs defaultValue="shipments" className="gap-6">
        <TabsList>
          <TabsTrigger value="shipments">Shipments</TabsTrigger>
          <TabsTrigger value="blocked-carriers">Blocked carriers</TabsTrigger>
        </TabsList>

        <TabsContent value="shipments" className="space-y-6">
          <ListFilters
            count={resultCount(data?.total, 'shipment')}
            active={status === 'all' ? 0 : 1}
            onClear={() => void setFilters({ status: null, page: 1 })}
            search={
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <SearchInput
                  placeholder="Search by PO number, tracking, carrier..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value)
                    void setFilters({ page: 1 })
                  }}
                  className="pl-9"
                />
              </div>
            }
          >
            <ListFilter label="Status">
              <Select
                value={status}
                onValueChange={(v) => {
                  void setFilters({ status: v, page: 1 })
                }}
              >
                <SelectTrigger aria-label="Shipment status filter" className="w-full">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s === 'all' ? 'All Statuses' : formatStatus(s)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </ListFilter>
          </ListFilters>

          {isLoading ? (
            <ShipmentsTableSkeleton />
          ) : !data?.items?.length ? (
            <p className="py-10 text-center text-sm text-muted-foreground">No shipments found</p>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Project</TableHead>
                      <TableHead>Shipment</TableHead>
                      <TableHead>Dates</TableHead>
                      <TableHead>Products</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.items.map((shipment) => {
                      const po = shipment.purchase_order
                      const project = po?.internal_order?.project
                      const customerGroup = po?.internal_order?.customer_group
                      const trackingRef = shipment.tracking_numbers || shipment.pro_number
                      const shipmentProducts = (shipment.line_items ?? []).map(
                        shipmentLineItemToProductSummaryItem,
                      )
                      return (
                        <TableRow key={shipment.id}>
                          <TableCell className="align-top">
                            <div className="space-y-0.5">
                              {project ? (
                                <Link
                                  to="/projects/$projectId"
                                  params={{ projectId: project.uuid }}
                                  className="font-medium text-primary hover:underline"
                                >
                                  {project.name}
                                </Link>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                              {customerGroup && (
                                <p className="text-xs">
                                  <Link
                                    to="/customers/$groupId"
                                    params={{ groupId: customerGroup.uuid }}
                                    className="text-muted-foreground hover:text-primary hover:underline"
                                  >
                                    {customerGroup.name}
                                  </Link>
                                </p>
                              )}
                              {po && (
                                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                  <Link
                                    to="/purchase-orders/$poId"
                                    params={{ poId: po.uuid }}
                                    className="font-mono hover:text-primary hover:underline"
                                  >
                                    {po.po_number}
                                  </Link>
                                  {po.manufacturer && (
                                    <>
                                      <span>·</span>
                                      <Link
                                        to="/manufacturers/$manuId"
                                        params={{ manuId: po.manufacturer.uuid }}
                                        className="hover:text-primary hover:underline"
                                      >
                                        {po.manufacturer.short_name}
                                      </Link>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="align-top">
                            <div className="space-y-1">
                              <Link
                                to="/shipments/$shipmentId"
                                params={{ shipmentId: shipment.uuid }}
                                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                              >
                                <Truck className="h-3.5 w-3.5" />
                                {trackingRef ? (
                                  <span className="font-mono text-xs">{trackingRef}</span>
                                ) : (
                                  <span>View shipment</span>
                                )}
                              </Link>
                              <div className="flex flex-wrap items-center gap-1.5">
                                <Badge
                                  variant={shipmentStatusVariant(shipment.status)}
                                  className="text-xs"
                                >
                                  {formatStatus(shipment.status)}
                                </Badge>
                                {shipment.carrier_name && (
                                  <span className="text-xs text-muted-foreground">
                                    {shipment.carrier_name}
                                  </span>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="align-top">
                            <ShipmentDateBadges shipment={shipment} />
                            {!shipment.estimated_ship_date &&
                              !shipment.estimated_delivery_date &&
                              !shipment.actual_ship_date && (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                          </TableCell>
                          <TableCell className="min-w-0 max-w-[360px] whitespace-normal align-top">
                            <ProductSummaryList
                              items={shipmentProducts}
                              moreHref={{
                                to: '/shipments/$shipmentId',
                                params: { shipmentId: shipment.uuid },
                              }}
                            />
                          </TableCell>
                          <TableCell className="text-right align-top tabular-nums">
                            {shipment.shipping_cost ? formatMoney(shipment.shipping_cost) : '-'}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile card list */}
              <div className="space-y-2 md:hidden">
                {data.items.map((shipment) => {
                  const po = shipment.purchase_order
                  const project = po?.internal_order?.project
                  const customerGroup = po?.internal_order?.customer_group
                  const trackingRef = shipment.tracking_numbers || shipment.pro_number
                  const shipmentProducts = (shipment.line_items ?? []).map(
                    shipmentLineItemToProductSummaryItem,
                  )
                  return (
                    <Link
                      key={shipment.id}
                      to="/shipments/$shipmentId"
                      params={{ shipmentId: shipment.uuid }}
                      className="block rounded-lg border bg-card p-3 transition-colors hover:bg-muted/40"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          {project ? (
                            <p className="truncate font-medium text-primary">{project.name}</p>
                          ) : (
                            <p className="truncate font-medium">Shipment</p>
                          )}
                          {customerGroup && (
                            <p className="truncate text-xs text-muted-foreground">
                              {customerGroup.name}
                            </p>
                          )}
                          {po && (
                            <p className="truncate text-xs text-muted-foreground">
                              <span className="font-mono">{po.po_number}</span>
                              {po.manufacturer && <> · {po.manufacturer.short_name}</>}
                            </p>
                          )}
                        </div>
                        <div className="shrink-0 text-right">
                          <Badge variant={shipmentStatusVariant(shipment.status)} className="text-[10px]">
                            {formatStatus(shipment.status)}
                          </Badge>
                          {shipment.shipping_cost ? (
                            <p className="mt-1 text-xs font-semibold tabular-nums">
                              {formatMoney(shipment.shipping_cost)}
                            </p>
                          ) : null}
                        </div>
                      </div>
                      {shipmentProducts.length > 0 && (
                        <div className="mt-2 border-t pt-2">
                          <ProductSummaryList items={shipmentProducts} linkEntities={false} />
                        </div>
                      )}
                      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
                        {trackingRef && (
                          <span className="font-mono text-[11px] text-muted-foreground">
                            {trackingRef}
                          </span>
                        )}
                        {shipment.carrier_name && (
                          <span className="text-[11px] text-muted-foreground">
                            {shipment.carrier_name}
                          </span>
                        )}
                      </div>
                      <ShipmentDateBadges shipment={shipment} className="mt-2" />
                    </Link>
                  )
                })}
              </div>
            </>
          )}

          <div className="flex items-center justify-between px-4 py-3">
            <p className="text-sm text-muted-foreground">
              Page {data?.page ?? 1} of {data?.pages ?? 1} ({data?.total ?? 0} total)
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => void setFilters({ page: Math.max(1, page - 1) })}
                disabled={page <= 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void setFilters({ page: page + 1 })}
                disabled={page >= (data?.pages ?? 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="blocked-carriers">
          <BlockedCarriersPanel />
        </TabsContent>
      </Tabs>
    </div>
  )
}
